import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';
import { getSyncEvents } from '../api';
import type { SyncEvent, SyncEventFilters, PaginatedResponse } from '../types';

/**
 * Global Data Cache Context
 * 
 * Provides app-wide caching for sync events data that persists across navigation.
 * This prevents constant reloading when switching between pages.
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface AllEventsCache {
  events: SyncEvent[];
  timestamp: number;
  timeRange: string;
}

interface DataCacheState {
  // Cache for paginated queries (Events page)
  paginatedCache: Map<string, CacheEntry<PaginatedResponse<SyncEvent>>>;
  // Cache for "all events" queries (Users/Devices pages)
  allEventsCache: AllEventsCache | null;
  // Last fetch times for display
  lastFetchTimes: Map<string, Date>;
}

interface DataCacheContextValue {
  // Fetch paginated events with caching
  fetchPaginatedEvents: (
    filters: SyncEventFilters,
    forceRefresh?: boolean
  ) => Promise<PaginatedResponse<SyncEvent> | null>;
  
  // Fetch all events (for aggregation pages) with caching
  fetchAllEvents: (
    timeRange: string,
    forceRefresh?: boolean,
    onProgress?: (count: number) => void
  ) => Promise<SyncEvent[]>;
  
  // Check if we have cached all-events data
  hasAllEventsCache: (timeRange: string) => boolean;
  
  // Get cached all-events data without fetching
  getCachedAllEvents: (timeRange: string) => SyncEvent[] | null;
  
  // Clear all caches (for full refresh)
  clearAllCaches: () => void;
  
  // Get last fetch time for a cache key
  getLastFetchTime: (key: string) => Date | null;
  
  // Loading states
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;
}

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

const DataCacheContext = createContext<DataCacheContextValue | null>(null);

export function DataCacheProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const cacheRef = useRef<DataCacheState>({
    paginatedCache: new Map(),
    allEventsCache: null,
    lastFetchTimes: new Map(),
  });

  const getCacheKey = (filters: SyncEventFilters): string => {
    return JSON.stringify(filters);
  };

  const isCacheValid = (timestamp: number): boolean => {
    return Date.now() - timestamp < CACHE_TTL;
  };

  /**
   * Fetch paginated events with caching (for Events page)
   */
  const fetchPaginatedEvents = useCallback(async (
    filters: SyncEventFilters,
    forceRefresh = false
  ): Promise<PaginatedResponse<SyncEvent> | null> => {
    const cacheKey = getCacheKey(filters);
    
    // Check cache first
    if (!forceRefresh) {
      const cached = cacheRef.current.paginatedCache.get(cacheKey);
      if (cached && isCacheValid(cached.timestamp)) {
        return cached.data;
      }
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await getSyncEvents(filters);
      
      // Update cache
      cacheRef.current.paginatedCache.set(cacheKey, {
        data: response,
        timestamp: Date.now(),
      });
      cacheRef.current.lastFetchTimes.set('paginated', new Date());
      
      return response;
    } catch (err) {
      console.error('[DataCache] Error fetching paginated events:', err);
      setError('Failed to load events');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Fetch ALL events with caching (for Users/Devices aggregation pages)
   * This fetches all pages and caches the complete result
   */
  const fetchAllEvents = useCallback(async (
    timeRange: string,
    forceRefresh = false,
    onProgress?: (count: number) => void
  ): Promise<SyncEvent[]> => {
    // Check cache first
    if (!forceRefresh) {
      const cached = cacheRef.current.allEventsCache;
      if (cached && cached.timeRange === timeRange && isCacheValid(cached.timestamp)) {
        return cached.events;
      }
    }

    setIsLoading(true);
    setLoadingMessage('Loading events...');
    setError(null);

    try {
      const filters: SyncEventFilters = {
        page_size: 1000,
        ordering: '-timestamp',
      };

      // Apply time range filter
      if (timeRange) {
        const now = new Date();
        let since: Date;
        switch (timeRange) {
          case '1h':
            since = new Date(now.getTime() - 60 * 60 * 1000);
            break;
          case '24h':
            since = new Date(now.getTime() - 24 * 60 * 60 * 1000);
            break;
          case '7d':
            since = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            since = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          default:
            since = new Date(0);
        }
        filters.timestamp_gte = since.toISOString();
      }

      // Paginate through ALL events
      let allEvents: SyncEvent[] = [];
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        setLoadingMessage(`Loading events... (${allEvents.length.toLocaleString()} fetched)`);
        onProgress?.(allEvents.length);
        
        const response = await getSyncEvents({ ...filters, page });
        const events = response.results || [];
        allEvents = [...allEvents, ...events];
        
        hasMore = response.next !== null && events.length > 0;
        page++;
        
        // Safety limit: max 50 pages = 50,000 events
        if (page > 50) break;
      }
      
      // Update cache
      cacheRef.current.allEventsCache = {
        events: allEvents,
        timestamp: Date.now(),
        timeRange,
      };
      cacheRef.current.lastFetchTimes.set('allEvents', new Date());
      
      setLoadingMessage('');
      return allEvents;
    } catch (err) {
      console.error('[DataCache] Error fetching all events:', err);
      setError('Failed to load events');
      return [];
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  }, []);

  /**
   * Check if we have valid cached all-events data
   */
  const hasAllEventsCache = useCallback((timeRange: string): boolean => {
    const cached = cacheRef.current.allEventsCache;
    return cached !== null && cached.timeRange === timeRange && isCacheValid(cached.timestamp);
  }, []);

  /**
   * Get cached all-events data without fetching
   */
  const getCachedAllEvents = useCallback((timeRange: string): SyncEvent[] | null => {
    const cached = cacheRef.current.allEventsCache;
    if (cached && cached.timeRange === timeRange && isCacheValid(cached.timestamp)) {
      return cached.events;
    }
    return null;
  }, []);

  /**
   * Clear all caches
   */
  const clearAllCaches = useCallback(() => {
    cacheRef.current.paginatedCache.clear();
    cacheRef.current.allEventsCache = null;
    cacheRef.current.lastFetchTimes.clear();
  }, []);

  /**
   * Get last fetch time for display
   */
  const getLastFetchTime = useCallback((key: string): Date | null => {
    return cacheRef.current.lastFetchTimes.get(key) || null;
  }, []);

  return (
    <DataCacheContext.Provider value={{
      fetchPaginatedEvents,
      fetchAllEvents,
      hasAllEventsCache,
      getCachedAllEvents,
      clearAllCaches,
      getLastFetchTime,
      isLoading,
      loadingMessage,
      error,
    }}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}
